import { EditOutlined } from '@ant-design/icons';
import type { ButtonType } from 'antd/es/button';
import { createElement } from 'react';

import type { ChannelManager } from 'app/typings/channelManagers';
import {
  capitalizeFirstLetter,
  parseUppercaseSnakeTextToCapitalize,
} from 'app/utils/string';

import type { ActionButton } from './InventoryActions/InventoryActions';
import type {
  AutoInventoryConfig,
  InventoryControlsProps,
} from './InventoryControls/InventoryControls';
import { PackType } from './InventoryToggle/InventoryToggle';

export enum BUTTON_KEY {
  SAVE = 'SAVE',
  EDIT = 'EDIT',
  CANCEL = 'CANCEL',
  PRICING_RULE = 'PRICING_RULE',
  PRICING_CONFIGURATION = 'PRICING_CONFIGURATION',
  AUTO_INVENTORY = 'AUTO_INVENTORY',
}

const DEFAULT_LABELS: Record<BUTTON_KEY, string> = {
  [BUTTON_KEY.SAVE]: 'Save',
  [BUTTON_KEY.EDIT]: 'Edit',
  [BUTTON_KEY.CANCEL]: 'Cancel',
  [BUTTON_KEY.PRICING_RULE]: 'Pricing Rule',
  [BUTTON_KEY.PRICING_CONFIGURATION]: 'Configure Pricing',
  [BUTTON_KEY.AUTO_INVENTORY]: 'Automate inventory',
};

const createButton = (
  key: BUTTON_KEY,
  onClick: () => void,
  options?: {
    disabled?: boolean;
    loading?: boolean;
    label?: string;
    icon?: React.ReactNode;
    type?: ButtonType;
  }
): ActionButton => ({
  key,
  label: options?.label || DEFAULT_LABELS[key],
  onClick,
  ...(options?.icon && { icon: options.icon }),
  ...(options?.type && { type: options.type }),
  ...(options?.disabled !== undefined && { disabled: options.disabled }),
  ...(options?.loading !== undefined && { loading: options.loading }),
});

export const createSaveButton = (
  onClick: () => void,
  options?: {
    disabled?: boolean;
    loading?: boolean;
    label?: string;
  }
): ActionButton =>
  createButton(BUTTON_KEY.SAVE, onClick, { ...options, type: 'primary' });

export const createCancelButton = (
  onClick: () => void,
  options?: {
    disabled?: boolean;
    label?: string;
  }
): ActionButton => createButton(BUTTON_KEY.CANCEL, onClick, options);

export const createEditButton = (
  onClick: () => void,
  options?: {
    disabled?: boolean;
    label?: string;
    icon?: React.ReactNode;
    type?: ButtonType;
  }
): ActionButton =>
  createButton(BUTTON_KEY.EDIT, onClick, {
    ...options,
    type: options?.type ?? 'primary',
  });

export const createPricingRuleButton = (
  onClick: () => void,
  options?: {
    disabled?: boolean;
    label?: string;
    icon?: React.ReactNode;
  }
): ActionButton => createButton(BUTTON_KEY.PRICING_RULE, onClick, options);

export const createPricingConfigurationButton = (
  onClick: () => void,
  options?: {
    disabled?: boolean;
    label?: string;
    icon?: React.ReactNode;
  }
): ActionButton =>
  createButton(BUTTON_KEY.PRICING_CONFIGURATION, onClick, options);

export const createAutoInventoryButton = (
  onClick: () => void,
  options?: {
    icon?: React.ReactNode;
  }
): ActionButton => createButton(BUTTON_KEY.AUTO_INVENTORY, onClick, options);

export const createEditModeConfig = (
  onCancel: () => void,
  onSave: () => void,
  options?: {
    saveDisabled?: boolean;
    saving?: boolean;
  }
): ActionButton[] => [
  createCancelButton(onCancel, { disabled: options?.saving }),
  createSaveButton(onSave, {
    disabled: options?.saveDisabled,
    loading: options?.saving,
  }),
];

export const createViewModeEditOnlyConfig = (
  onEdit: () => void,
  options?: {
    icon?: React.ReactNode;
    disabled?: boolean;
    label?: string;
    type?: ButtonType;
  }
): ActionButton[] => [
  createEditButton(onEdit, {
    icon: options?.icon,
    disabled: options?.disabled,
    label: options?.label,
    type: options?.type ?? 'primary',
  }),
];

export const createViewModeRoomsConfig = (
  onEdit: () => void,
  onPricingConfiguration: () => void,
  hasChannelManager: boolean,
  options?: {
    pricingConfigurationIcon?: React.ReactNode;
  }
): ActionButton[] => {
  return [
    createPricingConfigurationButton(onPricingConfiguration, {
      icon: options?.pricingConfigurationIcon,
    }),
    createEditButton(onEdit),
  ]?.filter(Boolean) as ActionButton[];
};

export const createViewModePricingConfigurationOnlyConfig = (
  onPricingConfiguration: () => void,
  options?: {
    pricingConfigurationIcon?: React.ReactNode;
    disabled?: boolean;
  }
): ActionButton[] => [
  createPricingConfigurationButton(onPricingConfiguration, {
    icon: options?.pricingConfigurationIcon,
    disabled: options?.disabled,
  }),
];

type ToggleOptions = {
  value: PackType;
  onChange?: (value: PackType) => void;
  nightStayLabel?: string;
  daytimeLabel?: string;
};

export const createPacksTabConfig = (
  isEditMode: boolean,
  toggle: ToggleOptions,
  handlers: {
    onEdit?: () => void;
    onCancel?: () => void;
    onSave?: () => void;
    onPricingConfiguration: () => void;
  },
  options?: {
    saveDisabled?: boolean;
    saving?: boolean;
    icon?: React.ReactNode;
    pricingConfigurationIcon?: React.ReactNode;
  }
): InventoryControlsProps => {
  const toggleConfig = {
    value: toggle.value,
    onChange: toggle.onChange,
    options: [
      {
        label: parseUppercaseSnakeTextToCapitalize(PackType.NIGHT_STAY),
        value: PackType.NIGHT_STAY,
      },
      {
        label: capitalizeFirstLetter(PackType.DAYTIME),
        value: PackType.DAYTIME,
      },
    ],
  };

  if (!isEditMode) {
    if (!handlers.onEdit) {
      throw new Error('VIEW mode requires onEdit handler');
    }

    const isDaytime = toggle.value === PackType.DAYTIME;

    return {
      toggle: toggleConfig,
      actions: {
        buttons: [
          ...(!isDaytime
            ? createViewModePricingConfigurationOnlyConfig(
                handlers.onPricingConfiguration,
                { pricingConfigurationIcon: options?.pricingConfigurationIcon }
              )
            : []),
          ...createViewModeEditOnlyConfig(handlers.onEdit, {
            icon: options?.icon ?? createElement(EditOutlined),
            label: 'Max booking',
            type: 'default',
          }),
        ],
      },
    };
  }

  if (!handlers.onCancel || !handlers.onSave) {
    throw new Error('EDIT mode requires onCancel and onSave handlers');
  }

  return {
    toggle: toggleConfig,
    actions: {
      buttons: createEditModeConfig(handlers.onCancel, handlers.onSave, {
        saveDisabled: options?.saveDisabled,
        saving: options?.saving,
      }),
    },
  };
};

export const createRoomsTabConfig = (
  isEditMode: boolean,
  handlers: {
    onEdit?: () => void;
    onCancel?: () => void;
    onSave?: () => void;
    onPricingConfiguration?: () => void;
    onAutoInventory?: () => void;
  },
  hasChannelManager: boolean,
  channelManager?: ChannelManager,
  options?: {
    saveDisabled?: boolean;
    saving?: boolean;
    pricingConfigurationIcon?: React.ReactNode;
    autoInventoryIcon?: React.ReactNode;
  }
): InventoryControlsProps => {
  if (!isEditMode) {
    if (!handlers.onEdit || !handlers.onPricingConfiguration) {
      throw new Error(
        'VIEW mode requires  onEdit handler and onPricingConfiguration handlers'
      );
    }

    return {
      autoInventory: handlers.onAutoInventory
        ? ({
            onClick: handlers.onAutoInventory,
            icon: options?.autoInventoryIcon,
          } satisfies AutoInventoryConfig)
        : undefined,
      actions: {
        buttons: createViewModeRoomsConfig(
          handlers.onEdit,
          handlers.onPricingConfiguration,
          hasChannelManager,
          {
            pricingConfigurationIcon: options?.pricingConfigurationIcon,
          }
        ),
      },
      channelManager: hasChannelManager ? channelManager : undefined,
    };
  }

  if (!handlers.onCancel || !handlers.onSave) {
    throw new Error('EDIT mode requires onCancel and onSave handlers');
  }

  return {
    actions: {
      buttons: createEditModeConfig(handlers.onCancel, handlers.onSave, {
        saveDisabled: options?.saveDisabled,
        saving: options?.saving,
      }),
    },
    channelManager: hasChannelManager ? channelManager : undefined,
  };
};
export const createExperiencesTabConfig = (
  isEditMode: boolean,
  handlers: {
    onEdit?: () => void;
    onCancel?: () => void;
    onSave?: () => void;
  },
  options?: {
    saveDisabled?: boolean;
    saving?: boolean;
    icon?: React.ReactNode;
  },
  disableActions = false
): InventoryControlsProps => {
  if (!isEditMode) {
    if (!handlers.onEdit) {
      throw new Error('VIEW mode requires onEdit handlers');
    }

    return {
      actions: {
        buttons: createViewModeEditOnlyConfig(handlers.onEdit, {
          icon: options?.icon,
          disabled: disableActions,
        }),
      },
    };
  }

  if (!handlers.onCancel || !handlers.onSave) {
    throw new Error('EDIT mode requires onCancel and onSave handlers');
  }

  return {
    actions: {
      buttons: createEditModeConfig(handlers.onCancel, handlers.onSave, {
        saveDisabled: options?.saveDisabled,
        saving: options?.saving,
      }),
    },
  };
};
